import { isNil } from '@core/utils/shared-utils';

import { deliveryColor, itemsColors, pickupColor, stringToColor } from '../../colors.model';
import { CustomData } from './custom-data';
import { RecurringWindow } from './time-window';
import { UserSiteV1Model } from './user-site-v1';

export type ScheduleKind = 'SiteOpening' | 'SpecificOperation' | 'Pickup' | 'Delivery';

export function isSiteOpening(schKind: ScheduleKind): boolean {
  return schKind === 'SiteOpening';
}

export function isPickupSchedule(schKind: ScheduleKind): boolean {
  return schKind === 'Pickup';
}

export function isDeliverySchedule(schKind: ScheduleKind): boolean {
  return schKind === 'Delivery';
}

export function isPureDeliverySchedule(schKinds: ScheduleKind[]): boolean {
  if (schKinds.length !== 1) {
    return false;
  }
  const schKind = schKinds[0];
  return schKind === 'Delivery';
}

export function isPurePickupSchedule(schKinds: ScheduleKind[]): boolean {
  if (schKinds.length !== 1) {
    return false;
  }
  const schKind = schKinds[0];
  return schKind === 'Pickup';
}

export interface SiteSchedule {
  uid: string;
  name?: string | null;
  kinds?: ScheduleKind[];
  compatible_commodity_uids?: string[];
  compatible_job_kind_uids?: string[];
  compatible_flow_kind_uids?: string[];
  compatible_pickup_site_uids?: string[];
  compatible_delivery_site_uids?: string[];
  window: RecurringWindow;
  custom_data?: CustomData;
}

export function isScheduleCompatibleWithPickup(schedule: SiteSchedule): boolean {
  if (isNil(schedule.kinds)) {
    return true;
  }
  return !isNil(schedule.kinds.find(isPickupSchedule));
}

export function isScheduleCompatibleWithDelivery(schedule: SiteSchedule): boolean {
  if (isNil(schedule.kinds)) {
    return true;
  }
  return !isNil(schedule.kinds.find(isDeliverySchedule));
}

export function makeScheduleColor(sch: SiteSchedule): string {
  if (
    sch.compatible_commodity_uids?.map((item) => item.toLowerCase()).includes('surgele')
  ) {
    return itemsColors.blue;
  }
  if (sch.compatible_commodity_uids?.map((item) => item.toLowerCase()).includes('frais')) {
    return itemsColors.lightBlue;
  }
  return stringToColor(sch.compatible_commodity_uids?.join(',') ?? sch.name ?? sch.uid);
}

export function makeScheduleBackgroundColor(
  sch: SiteSchedule,
  auxSites: Record<string, UserSiteV1Model>,
  thisSiteColor?: string
): string | undefined {
  if (
    !isNil(sch.kinds) &&
    isPureDeliverySchedule(sch.kinds) &&
    !isNil(sch.compatible_pickup_site_uids) &&
    sch.compatible_pickup_site_uids.length === 1
  ) {
    const siteColor = auxSites[sch.compatible_pickup_site_uids[0] ?? '']?.color;
    if (!isNil(siteColor)) {
      return siteColor;
    }
  }

  if (
    !isNil(sch.kinds) &&
    isPurePickupSchedule(sch.kinds) &&
    !isNil(sch.compatible_delivery_site_uids) &&
    sch.compatible_delivery_site_uids.length === 1
  ) {
    const siteColor = auxSites[sch.compatible_delivery_site_uids[0] ?? '']?.color;
    if (!isNil(siteColor)) {
      return siteColor;
    }
  }
  if (!isNil(thisSiteColor)) {
    return thisSiteColor;
  }

  return undefined;
}

export function makeScheduleBorderColor(sch: SiteSchedule): string | undefined {
  if (!isNil(sch.kinds) && isPurePickupSchedule(sch.kinds)) {
    return pickupColor;
  } else if (!isNil(sch.kinds) && isPureDeliverySchedule(sch.kinds)) {
    return deliveryColor;
  }
  return;
}
